import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardActionArea,
  CardContent,
  TextField,
  Autocomplete,
  alpha,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import Disclaimer from '@/components/common/Disclaimer';
import { useFilterOptions } from '@/hooks/useFilterOptions';
import { usePageTitle } from '@/hooks/usePageTitle';

// Popular tickers to display on landing page
const POPULAR_TICKERS = [
  'NVDA',
  'TSLA',
  'AAPL',
  'AMD',
  'META',
  'GOOGL',
  'MSFT',
  'AMZN',
  'SPY',
  'QQQ',
  'PLTR',
  'COIN',
];

export default function TickerTrackingPage() {
  usePageTitle('标的追踪');

  const navigate = useNavigate();
  const { data: filterOptions } = useFilterOptions();
  const [searchValue, setSearchValue] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState('');

  const handleTickerSelect = (ticker: string) => {
    navigate(`/${ticker.toUpperCase()}`);
  };

  const handleSearchChange = (_: React.SyntheticEvent, value: string | null) => {
    if (value) {
      handleTickerSelect(value);
      setSearchValue(null);
      setInputValue('');
    }
  };

  const handleInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      handleTickerSelect(inputValue.trim());
      setSearchValue(null);
      setInputValue('');
    }
  };

  return (
    <Box>
      {/* Hero Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
          标的追踪
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
          追踪特定标的的历史博主观点，了解市场情绪变化趋势
        </Typography>
        <Disclaimer />

        {/* Search Box */}
        <Autocomplete
          freeSolo
          options={filterOptions?.tickers || []}
          value={searchValue}
          onChange={handleSearchChange}
          inputValue={inputValue}
          onInputChange={(_, newValue) => setInputValue(newValue)}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="输入标的代码，如 NVDA、TSLA、AAPL..."
              variant="outlined"
              onKeyDown={handleInputKeyDown}
              sx={{
                maxWidth: 500,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />
          )}
          sx={{ mb: 4 }}
        />
      </Box>

      {/* Popular Tickers Grid */}
      <Box>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
          热门标的
        </Typography>
        <Grid container spacing={2}>
          {POPULAR_TICKERS.map((ticker) => (
            <Grid key={ticker} xs={6} sm={4} md={3} lg={2}>
              <Card
                elevation={0}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: 'primary.main',
                    transform: 'translateY(-2px)',
                    boxShadow: 2,
                  },
                }}
              >
                <CardActionArea onClick={() => handleTickerSelect(ticker)}>
                  <CardContent
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 1,
                      py: 2.5,
                    }}
                  >
                    <TrendingUpIcon
                      sx={{
                        fontSize: 20,
                        color: alpha('#2AA84A', 0.7),
                      }}
                    />
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        letterSpacing: '0.5px',
                      }}
                    >
                      {ticker}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Feature Description */}
      <Box sx={{ mt: 5, p: 3, bgcolor: 'action.hover', borderRadius: 2 }}>
        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
          功能说明
        </Typography>
        <Typography variant="body2" color="text.secondary">
          • 查看特定标的在不同时间段内的博主观点汇总
          <br />
          • 支持按时间范围筛选：最近一周、一月、三月或全部
          <br />
          • 支持按博主筛选，只看关注的博主观点
          <br />
          • 需要登录后才能查看详细观点内容
        </Typography>
      </Box>
    </Box>
  );
}
